import { isNil, omitBy } from 'lodash';
import { isValidApplication } from 'utils/applicationUtility';
import { useAnalytics } from 'use-analytics';
import { useApplication, useClient, useLatestLoan } from 'hooks/webapi';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import TagManager from 'react-gtm-module';
import useDataLayerHashedValues from './useDataLayerHashedValues';

const tagManagerArgs = {
  gtmId: import.meta.env.VITE_GTM || '',
};

export default function useDataLayer() {
  const { client } = useClient();
  const { application } = useApplication();
  const { latestLoan } = useLatestLoan();
  const { identify, page, track } = useAnalytics();
  const location = useLocation();
  const prevLocation = useRef<string | null>(undefined);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const {
    getHashedEmail,
    getHashedFirstName,
    getHashedLastName,
    getHashedTelephoneNumber,
  } = useDataLayerHashedValues();

  const logPageChange = async () => {
    try {
      const dataLayer: Record<string, any> = {
        page_location: window.location.href,
        page_referrer: prevLocation.current ?? document.referrer,
        page_title: document.title,
        page: {
          url: window.location.pathname,
          referrer: prevLocation.current ?? document.referrer,
        },
      };

      if (client) {
        dataLayer.client = omitBy(
          {
            id: client.id,
            dateOfBirth: client.dateOfBirth,
            number: client.number,
            status: client.status,
            age: client.age,
            identifiedBy: client.identifiedBy,
            registeredBy: client.registeredBy,
            htn: await getHashedTelephoneNumber(
              client?.mobilePhone.mobilePhone,
            ),
            hma: await getHashedEmail(client?.email.email),
            address: omitBy(
              {
                hfn: await getHashedFirstName(client.firstName),
                hln: await getHashedLastName(client.lastName),
                city: client.declaredAddress?.location1,
                'postal-code': client.declaredAddress?.postalCode,
              },
              isNil,
            ),
          },
          isNil,
        );
      }

      if (application) {
        if (isValidApplication(application, client)) {
          dataLayer.application = omitBy(
            {
              amount: application.amount,
              id: application.id,
              status: application.status,
              term: application.term,
              type: application.type,
            },
            isNil,
          );
        } else {
          dataLayer.application = omitBy(
            {
              status: application.status,
              resolution: application.resolution,
              resolutionDetail: application.resolutionDetail,
              resolutionMessage: application.resolutionMessage,
            },
            isNil,
          );
        }
      }

      if (latestLoan) {
        dataLayer.latestLoan = omitBy(
          {
            loanNumber: latestLoan.loanNumber,
            amount: latestLoan.amount,
            term: latestLoan.loanTerm.value,
            overdueDays: latestLoan.overDueDays,
            dueDate: latestLoan.dueDate,
          },
          isNil,
        );
      }

      TagManager.dataLayer({
        dataLayer: {
          event: 'pageChange',
          ...dataLayer,
        },
      });

      TagManager.dataLayer({
        dataLayer: {
          event: 'page_view',
          ...dataLayer,
        },
      });

      prevLocation.current = window.location.pathname;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Error logging page change do GTM dataLayer', e);
    }
  };

  const decouncedLogPageChange = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(logPageChange, 2000);
  };

  // Track client and latestLoan changes
  useEffect(() => {
    (async function init() {
      if (client?.id) {
        track(
          client.id.toString(),
          {
            latestLoanNumber: latestLoan?.loanNumber,
            email: client?.email.email,
          },
          {},
        );
      }
    })();
  }, [client?.id, client?.email.email, latestLoan, latestLoan?.loanNumber]);

  // initialize client and application id (sessionStorage changes)
  useEffect(() => {
    identify('initialize', { client, application });
  }, [application?.id, client?.id]);

  // track page change
  useEffect(() => {
    page();
  }, [location?.pathname]);

  useEffect(() => {
    TagManager.initialize(tagManagerArgs);

    // Track basic JavaScript errors
    window.addEventListener('error', function (e) {
      TagManager.dataLayer({
        dataLayer: {
          event: 'error',
          message: e.message,
          stacktrace: e.error?.stack || 'error stack undefined',
          path: window.location.pathname,
        },
      });
    });

    if (
      import.meta.env.VITE_ENV === 'production' ||
      import.meta.env.VITE_ENV === 'staging' ||
      import.meta.env.VITE_ENV === 'test'
    ) {
      // Log the initial page load
      logPageChange();
    }
  }, []);

  useEffect(() => {
    decouncedLogPageChange();
  }, [location]);

  useEffect(() => {
    decouncedLogPageChange();

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [client, application, latestLoan]);
}
